import {
  Area,
  AreaChart,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { ProviderUsage, UsageDayPoint } from '@chefty/shared-types';
import { fmtCompact, fmtUsd } from '../lib/format';

// Warm field needs cool, distinct slices so providers stay legible side by side.
const PROVIDER_COLORS = ['#f4b14a', '#57c9b9', '#74b6f0', '#f0897a', '#b89cf0', '#9fd47a'];

const AXIS = { stroke: '#756a5c', fontSize: 11, fontFamily: 'Spline Sans Mono, monospace' };

function TooltipBox({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-cream-600/20 bg-ink-850/95 px-3 py-2 text-xs shadow-xl backdrop-blur">
      {children}
    </div>
  );
}

/** Tokens-per-day area, with cost on hover. */
export function UsageAreaChart({ series }: { series: UsageDayPoint[] }) {
  return (
    <ResponsiveContainer width="100%" height={240}>
      <AreaChart data={series} margin={{ top: 8, right: 8, bottom: 0, left: -8 }}>
        <defs>
          <linearGradient id="tok" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#f4b14a" stopOpacity={0.55} />
            <stop offset="100%" stopColor="#f4b14a" stopOpacity={0.02} />
          </linearGradient>
        </defs>
        <XAxis
          dataKey="date"
          tick={AXIS}
          tickLine={false}
          axisLine={{ stroke: '#33271d' }}
          tickFormatter={(d: string) => d.slice(5)}
          minTickGap={28}
        />
        <YAxis
          tick={AXIS}
          tickLine={false}
          axisLine={false}
          width={44}
          tickFormatter={(v: number) => fmtCompact(v)}
        />
        <Tooltip
          cursor={{ stroke: '#f4b14a', strokeOpacity: 0.3 }}
          content={({ active, payload, label }) =>
            active && payload?.length ? (
              <TooltipBox>
                <div className="mb-1 font-mono text-cream-400">{label}</div>
                <div className="tnum text-saffron-300">
                  {fmtCompact(Number(payload[0].payload.totalTokens))} tokens
                </div>
                <div className="tnum text-cream-200">
                  {fmtUsd(Number(payload[0].payload.estimatedCostUsd))}
                </div>
              </TooltipBox>
            ) : null
          }
        />
        <Area
          type="monotone"
          dataKey="totalTokens"
          stroke="#f4b14a"
          strokeWidth={2}
          fill="url(#tok)"
          animationDuration={700}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

/** Provider token-share donut with a legend. */
export function ProviderDonut({ data }: { data: ProviderUsage[] }) {
  const nonEmpty = data.filter((d) => d.totalTokens > 0);
  if (nonEmpty.length === 0) {
    return (
      <div className="grid h-[240px] place-items-center text-sm text-cream-400">
        No token usage recorded yet
      </div>
    );
  }
  return (
    <div className="flex items-center gap-5">
      <ResponsiveContainer width="55%" height={240}>
        <PieChart>
          <Pie
            data={nonEmpty}
            dataKey="totalTokens"
            nameKey="provider"
            innerRadius={58}
            outerRadius={92}
            paddingAngle={2}
            stroke="none"
            animationDuration={700}
          >
            {nonEmpty.map((d, i) => (
              <Cell key={d.provider} fill={PROVIDER_COLORS[i % PROVIDER_COLORS.length]} />
            ))}
          </Pie>
          <Tooltip
            content={({ active, payload }) =>
              active && payload?.length ? (
                <TooltipBox>
                  <div className="font-mono text-cream-100">{payload[0].name}</div>
                  <div className="tnum text-saffron-300">
                    {fmtCompact(Number(payload[0].value))} tokens
                  </div>
                </TooltipBox>
              ) : null
            }
          />
        </PieChart>
      </ResponsiveContainer>
      <ul className="flex-1 space-y-2">
        {nonEmpty.map((d, i) => (
          <li key={d.provider} className="flex items-center justify-between gap-2 text-sm">
            <span className="flex items-center gap-2">
              <span
                className="h-2.5 w-2.5 rounded-sm"
                style={{ background: PROVIDER_COLORS[i % PROVIDER_COLORS.length] }}
              />
              <span className="font-mono text-cream-200">{d.provider}</span>
            </span>
            <span className="tnum text-cream-400">{fmtCompact(d.totalTokens)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
